import classnames from 'classnames'
import * as React from 'react'

import { Icon } from '../icon'
import { PropsWithAs } from '../types'
import { TABLE_STICKY_CELL_CLASS_NAME } from './constants'
import { TableAlign } from './types'

const TABLE_HEADER_DEFAULT_ELEMENT = 'th'

interface SortProps {
  isActive: boolean
  direction: 1 | -1
}

function TableSortIndicator({ isActive, direction, className }: Partial<SortProps> & { className?: string }) {
  return (
    <Icon
      name="chevronDown"
      className={classnames(className, 'top-1/2 xl:absolute xl:-mt-2', {
        'hidden xl:invisible xl:block': !isActive,
        'rotate-180': direction === 1,
      })}
    />
  )
}

interface TableHeaderProps extends Omit<React.HTMLProps<HTMLTableCellElement>, 'as'> {
  isActive?: boolean
  align?: TableAlign
  hasPadding?: boolean
  hasHover?: boolean
  isSticky?: boolean
}

export function TableHeader<TagType extends React.ElementType = typeof TABLE_HEADER_DEFAULT_ELEMENT>({
  as: asComponent,
  align = TableAlign.Left,
  children,
  isActive,
  hasPadding = true,
  hasHover,
  isSticky,
  ...props
}: Omit<PropsWithAs<TagType>, 'align'> & TableHeaderProps) {
  const componentToRender = asComponent ?? TABLE_HEADER_DEFAULT_ELEMENT

  return React.createElement(
    componentToRender,
    {
      ...props,
      className: classnames(
        props.className,
        'whitespace-nowrap font-normal h-5 hover:first:rounded-l-md hover:last:rounded-r-md',
        {
          [TABLE_STICKY_CELL_CLASS_NAME]: isSticky,
          'cursor-pointer hover:select-none hover:bg-gray-200/30 dark:hover:bg-gray-500/30':
            hasHover && !props.disabled,
          'text-gray-500 dark:text-gray-300': props.disabled,
          'text-gray-600 dark:text-gray-200': !isActive && !props.disabled,
          'font-medium text-gray-800 dark:text-gray-50': isActive,
          'text-left': align === TableAlign.Left,
          'text-center': align === TableAlign.Center,
          'text-right': align === TableAlign.Right,
          'px-1': hasPadding,
          'p-0': !hasPadding,
        }
      ),
    },
    children
  )
}

export function TableHeaderSortable<TagType extends React.ElementType = typeof TABLE_HEADER_DEFAULT_ELEMENT>({
  direction,
  align = TableAlign.Left,
  ...props
}: Omit<PropsWithAs<TagType>, 'align'> & Partial<SortProps> & TableHeaderProps) {
  const isDisabled = props.disabled === true
  return (
    <TableHeader
      {...(props as any)}
      align={align}
      hasHover={true}
      className={classnames(props.className, {
        'hover:text-gray-800 dark:hover:text-gray-50': !props.isActive && !isDisabled,
        'xl:pl-4': align === TableAlign.Right,
        'xl:pr-4': align !== TableAlign.Right,
      })}
    >
      <div
        className={classnames('relative flex items-center xl:inline-flex', {
          'translate-x-1.5': align === TableAlign.Center && !props.isActive,
          'justify-end': align === TableAlign.Right,
        })}
      >
        {align === TableAlign.Right && (
          <TableSortIndicator isActive={props.isActive} direction={direction} className="right-full" />
        )}
        {props.children}
        {align !== TableAlign.Right && (
          <TableSortIndicator isActive={props.isActive} direction={direction} className="left-full" />
        )}
      </div>
    </TableHeader>
  )
}
